import React, { useEffect, useState } from 'react';
import apiClient from '../../api/client';
import { getResellerSiteUrl } from '../../utils/resellerSiteUrl';
import { FINOS_CATEGORIES, decodeCategories, encodeCategories, fetchMarketplaceCategories } from '../../utils/categories';
import { apiError, longDate, ratePct, rs, shortDate } from '../format';
import DataGrid from '../../components/Common/DataGrid';
import { Async, BucketPill, Button, EmptyState, FilterPills, Kpi, KpiGrid, PageHeader, Panel } from '../ui';
import { copyText } from '../clipboard';
import { useDash } from '../DashboardShell';
import useFetch from '../useFetch';
import { SiteLink } from './Overview';
import Select2 from '../../components/Common/Select2';
import { Stars } from '../../components/Public/Testimonials';
import { ProofButton } from '../Proof';

const BUCKETS = ['Approved', 'Pending', 'In progress', 'Declined'];

export const Customers = () => {
  const [bucket, setBucket] = useState('');
  const state = useFetch('/me/customers', { limit: 1000, ...(bucket ? { bucket } : {}) });
  const columns = [
    { title: 'Date', render: (c) => shortDate(c.date), value: (c) => c.date || '' },
    { title: 'Customer', render: (c) => <><strong>{c.name}</strong><small>{c.email}</small></>, value: (c) => `${c.name} ${c.email}` },
    { title: 'Product', render: (c) => <>{c.product}<small>{c.application_id}</small></>, value: (c) => `${c.product} ${c.application_id || ''}` },
    { title: 'Status', render: (c) => <BucketPill bucket={c.bucket} label={c.status} />, value: (c) => c.status },
    { title: 'Your commission', numeric: true, render: (c) => <strong>{c.commission > 0 ? rs(c.commission) : '–'}</strong>, value: (c) => c.commission },
  ];
  return (
    <>
      <PageHeader eyebrow="Your account" title="My customers" />
      <Panel>
        <Async state={state}>
          {(d) => (
            <>
              <div style={{ marginBottom: 14 }}>
                <FilterPills
                  label="Filter by status"
                  value={bucket}
                  onChange={setBucket}
                  options={[{ value: '', label: 'All', count: BUCKETS.reduce((n, b) => n + d.counts[b], 0) }, ...BUCKETS.map((b) => ({ value: b, label: b, count: d.counts[b] }))]}
                />
              </div>
              <DataGrid rows={d.items} columns={columns} order={[[0, 'desc']]} emptyText={bucket ? 'No customers with this status.' : 'No customers yet. When someone applies through your site, they appear here.'} />
              {d.total > d.items.length && <p className="ds-foot">Showing the newest {d.items.length} of {d.total}.</p>}
            </>
          )}
        </Async>
      </Panel>
    </>
  );
};

export const Earnings = () => {
  const overview = useFetch('/me/overview');
  const commissions = useFetch('/me/commissions', { limit: 1000 });
  const payouts = useFetch('/me/payouts', { limit: 1000 });
  const commissionCols = [
    { title: 'Date', render: (a) => shortDate(a.date), value: (a) => a.date || '' },
    { title: 'Product', render: (a) => <>{a.product}<small>{a.application_id}</small></>, value: (a) => `${a.product} ${a.application_id || ''}` },
    { title: 'Status', render: (a) => <BucketPill bucket={a.bucket} label={a.status} />, value: (a) => a.status },
    { title: 'Commission', numeric: true, render: (a) => <strong>{a.commission > 0 ? rs(a.commission) : '–'}</strong>, value: (a) => a.commission },
  ];
  const payoutCols = [
    { title: 'Date', render: (p) => longDate(p.paid_at), value: (p) => p.paid_at || '' },
    { title: 'Reference', render: (p) => <>{p.reference || '–'}{p.note && <small>{p.note}</small>}</>, value: (p) => `${p.reference || ''} ${p.note || ''}` },
    { title: 'Receipt', render: (p) => (p.has_proof ? <ProofButton url={`/me/payouts/${p.id}/proof`} label="View receipt" /> : <span className="ds-muted">–</span>) },
    { title: 'Amount', numeric: true, render: (p) => <strong>{rs(p.amount)}</strong>, value: (p) => p.amount },
  ];
  return (
    <>
      <PageHeader eyebrow="Your account" title="Commission and payouts" />
      <Async state={overview}>
        {(o) => (
          <KpiGrid>
            <Kpi label="Commission earned" value={rs(o.totals.commission_earned)} note="All time" />
            <Kpi label="Paid to you" value={rs(o.totals.commission_paid)} />
            <Kpi tone="accent" label="Waiting to be paid" value={rs(o.totals.commission_balance)} note="Earned minus paid" />
            <Kpi label="Your rate" value={ratePct(o.totals.commission_rate)} note="Set by the platform admin" />
          </KpiGrid>
        )}
      </Async>
      <div className="ds-row is-top">
        <Panel wide title="Commission per application" subtitle="Earned once, the first time an application is approved">
          <Async state={commissions}>
            {(d) => <DataGrid rows={d.items} columns={commissionCols} order={[[0, 'desc']]} emptyText="No commission yet. It appears here when one of your customers is approved." />}
          </Async>
        </Panel>
        <Panel title="Payments to you">
          <Async state={payouts}>
            {(d) => <DataGrid rows={d.items} columns={payoutCols} order={[[0, 'desc']]} emptyText="No payments yet. Each payout the platform sends you is recorded here with its reference." />}
          </Async>
        </Panel>
      </div>
    </>
  );
};

const RATINGS = [5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${'★'.repeat(n)}${'☆'.repeat(5 - n)}  (${n} of 5)` }));

/** Customer reviews you have collected. They appear on the public home page. */
const TestimonialsPanel = () => {
  const { notify, confirm } = useDash();
  const state = useFetch('/me/testimonials');
  const [form, setForm] = useState({ name: '', comment: '', rating: '5' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiClient.post('/me/testimonials', { name: form.name.trim(), comment: form.comment.trim(), rating: Number(form.rating) });
      setForm({ name: '', comment: '', rating: '5' });
      notify('Testimonial added. It now shows on the public home page.');
      state.reload();
    } catch (err) {
      setError(apiError(err, 'Could not add the testimonial'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    const ok = await confirm({ title: 'Remove this testimonial?', message: `“${t.comment}” by ${t.name} will no longer show on the home page.`, confirmLabel: 'Remove', danger: true });
    if (!ok) return;
    try {
      await apiClient.delete(`/me/testimonials/${t.id}`);
      notify('Testimonial removed');
      state.reload();
    } catch (err) {
      notify(apiError(err, 'Could not remove it'), 'error');
    }
  };

  return (
    <Panel title="Customer testimonials" subtitle="Add reviews from real customers. They are shown on the public home page, so only add genuine ones.">
      <div className="ds-row is-top">
        <form className="ds-form" onSubmit={add}>
          {error && <div className="ds-formerror" role="alert">{error}</div>}
          <div className="ds-field"><label htmlFor="tm-name">Customer name</label><input id="tm-name" className="ds-input" required maxLength={80} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
          <div className="ds-field"><label htmlFor="tm-comment">What they said</label><textarea id="tm-comment" className="ds-textarea" required minLength={5} maxLength={500} value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))} /></div>
          <div className="ds-field">
            <label htmlFor="tm-rating">Rating</label>
            <Select2 id="tm-rating" value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v || '5' }))} options={RATINGS} minimumResultsForSearch={Infinity} />
          </div>
          <div><Button variant="primary" type="submit" disabled={busy}>{busy ? 'Adding…' : 'Add testimonial'}</Button></div>
        </form>
        <Async state={state}>
          {(d) => d.items.length === 0 ? (
            <EmptyState title="No testimonials yet">Add your first one on the left.</EmptyState>
          ) : (
            <ul className="ds-steps" style={{ gap: 14 }}>
              {d.items.map((t) => (
                <li key={t.id} style={{ alignItems: 'flex-start', justifyContent: 'space-between', borderBottom: '1px solid #eef2f8', paddingBottom: 14 }}>
                  <div>
                    <Stars rating={t.rating} />
                    <p style={{ margin: '4px 0', fontWeight: 400 }}>“{t.comment}”</p>
                    <small className="ds-muted">{t.name} · {shortDate(t.date)}</small>
                  </div>
                  <Button size="sm" onClick={() => remove(t)}>Remove</Button>
                </li>
              ))}
            </ul>
          )}
        </Async>
      </div>
    </Panel>
  );
};

export const Share = () => {
  const { notify } = useDash();
  const state = useFetch('/me/profile');
  return (
    <>
      <PageHeader eyebrow="Your account" title="My link and sharing" />
      <Async state={state}>
        {(p) => {
          const url = getResellerSiteUrl(p.subdomain);
          const labels = decodeCategories(p.market_focus).map((id) => FINOS_CATEGORIES.find((c) => c.id === id)?.label).filter(Boolean);
          const message = `Compare ${labels.length ? labels.join(', ').toLowerCase() : 'savings, cards, loans and insurance'} side by side before you apply. ${p.business_name || p.name} has put it all in one place: ${url}`;
          const copy = async (text, ok) => notify((await copyText(text)) ? ok : 'Could not copy. Select the text and copy it.', 'info');
          return (
            <div className="ds-row">
              <Panel title="Your partner site" subtitle="Everyone who applies through this link is counted as your customer.">
                <SiteLink />
              </Panel>
              <Panel title="Ready-made message" subtitle="Edit it before you send it.">
                <p className="ds-message">{message}</p>
                <div className="ds-actions" style={{ marginTop: 14 }}>
                  <Button variant="primary" onClick={() => copy(message, 'Message copied')}>Copy message</Button>
                  <a className="ds-btn" target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(message)}`}>WhatsApp</a>
                  <a className="ds-btn" href={`mailto:?subject=${encodeURIComponent('Compare before you apply')}&body=${encodeURIComponent(message)}`}>Email</a>
                </div>
              </Panel>
            </div>
          );
        }}
      </Async>
      <TestimonialsPanel />
    </>
  );
};

export const Settings = () => {
  const { notify } = useDash();
  const state = useFetch('/me/profile');
  const [form, setForm] = useState(null);
  const [cats, setCats] = useState([]);
  const [options, setOptions] = useState(FINOS_CATEGORIES);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMarketplaceCategories(apiClient).then(setOptions).catch(() => {});
  }, []);

  useEffect(() => {
    const p = state.data;
    if (!p) return;
    setForm({ name: p.name || '', business_name: p.business_name || '', phone: p.phone || '', bank_name: p.bank_name || '', account_title: p.account_title || '', account_number: p.account_number || '' });
    setCats(p.market_focus ? decodeCategories(p.market_focus, options.map((o) => o.id)) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggle = (id) => setCats((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const save = async (e) => {
    e.preventDefault();
    if (!cats.length) return setError('Choose at least one category');
    setBusy(true);
    setError('');
    try {
      await apiClient.put('/me/profile', { ...form, market_focus: encodeCategories(cats, options.map((o) => o.id)) });
      notify('Settings saved');
      state.reload();
    } catch (err) {
      setError(apiError(err, 'Could not save your settings'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader eyebrow="Your account" title="Account settings" />
      <Async state={state}>
        {(p) => form && (
          <form className="ds-form" onSubmit={save}>
            {error && <div className="ds-formerror" role="alert">{error}</div>}
            <Panel title="Profile">
              <div className="ds-formgrid">
                <div className="ds-field"><label htmlFor="st-name">Full name</label><input id="st-name" className="ds-input" required value={form.name} onChange={set('name')} /></div>
                <div className="ds-field"><label htmlFor="st-biz">Business name</label><input id="st-biz" className="ds-input" value={form.business_name} onChange={set('business_name')} /></div>
                <div className="ds-field"><label htmlFor="st-phone">Phone</label><input id="st-phone" className="ds-input" value={form.phone} onChange={set('phone')} /></div>
                <div className="ds-field"><label htmlFor="st-email">Login email</label><input id="st-email" className="ds-input" value={p.email} readOnly aria-describedby="st-email-note" /><small id="st-email-note">To change your email, contact the platform admin.</small></div>
                <div className="ds-field"><label htmlFor="st-site">Your site</label><input id="st-site" className="ds-input" value={getResellerSiteUrl(p.subdomain, { withProtocol: false })} readOnly /></div>
              </div>
            </Panel>
            <Panel title="Categories" subtitle="Your partner site only shows these.">
              <div className="ds-checkgrid">
                {options.map((c) => (
                  <label key={c.id} className="ds-check">
                    <input type="checkbox" checked={cats.includes(c.id)} onChange={() => toggle(c.id)} />
                    {c.label}
                  </label>
                ))}
              </div>
            </Panel>
            <Panel title="Bank details for payouts" subtitle="Only you and the platform admin can see these.">
              <div className="ds-formgrid">
                <div className="ds-field"><label htmlFor="st-bank">Bank</label><input id="st-bank" className="ds-input" value={form.bank_name} onChange={set('bank_name')} /></div>
                <div className="ds-field"><label htmlFor="st-title">Account title</label><input id="st-title" className="ds-input" value={form.account_title} onChange={set('account_title')} /></div>
                <div className="ds-field"><label htmlFor="st-acct">Account number or IBAN</label><input id="st-acct" className="ds-input" value={form.account_number} onChange={set('account_number')} /></div>
              </div>
            </Panel>
            <div><Button variant="primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save changes'}</Button></div>
          </form>
        )}
      </Async>
    </>
  );
};

const FAQ = [
  ['How is my commission worked out?', 'When an application from your site is approved, you earn your rate (shown on Commission and payouts) on the approved amount. It is counted once per application.'],
  ['What do the statuses mean?', 'Approved: the application went through and you earned commission. In progress: the bank or insurer is still reviewing it. Pending: it was sent and has not been picked up yet. Declined: it was rejected or withdrawn, so no commission.'],
  ['When do I get paid?', 'The platform sends your balance to the bank account in your settings and records each payment, with its reference, under Commission and payouts. Add your bank details so nothing is held up.'],
  ['Why did my rate change?', 'Only the platform admin can change a rate. A change applies to applications approved after it. Commission you already earned is not recalculated.'],
  ['A customer says they applied but I cannot see them.', 'Customers appear when the application is reported back from finOS. Make sure they used your own link, then check My customers again later.'],
];

export const Support = () => (
  <>
    <PageHeader eyebrow="Your account" title="Support" />
    <Panel title="Common questions">
      <dl className="ds-dl" style={{ gridTemplateColumns: '1fr', gap: 4 }}>
        {FAQ.map(([q, a]) => (
          <React.Fragment key={q}>
            <dt style={{ color: '#1a1e2b', marginTop: 12 }}>{q}</dt>
            <dd style={{ color: '#3b4a5e' }}>{a}</dd>
          </React.Fragment>
        ))}
      </dl>
    </Panel>
    <p className="ds-callout is-info">Still stuck? Reply to the invitation email you received when your account was approved. The platform admin will help.</p>
  </>
);
